import { AsyncPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { BbitLanguage, BbitTranslationType, IBbitI18nSelectDataRow } from '@bbitgmbh/bbit.utils';
import { BbitWebShopFormatPricePipe } from '@bbitgmbh/webshop.sdk';
import { BbitOnlinePaymentService, BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { IBbitCampsite, IBbitCampsiteBookingPeriodWithCampsites } from '../camping.interfaces';
import { CampingBookingOverlayRef } from './camping-booking-overlay.ref';

const allPaymentMethods: IBbitI18nSelectDataRow[] = [
  {
    _id: '#specialCase',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Special case',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Spezialfall',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Cas spécial',
        },
      },
    },
  },
  {
    _id: '#invoice',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Invoice',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Rechnung',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Facture',
        },
      },
    },
  },
  {
    _id: '#collectiveInvoice',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Collective invoice',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Sammelrechnung',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Facture groupée',
        },
      },
    },
  },
  {
    _id: '#prepayment',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Prepayment',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Vorauszahlung',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Paiement anticipé',
        },
      },
    },
  },
  {
    _id: '#online-payment',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Online payment',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Online-Zahlung',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Paiement en ligne',
        },
      },
    },
  },
  {
    _id: '#cash-payment',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Cash payment',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Barzahlung',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Paiement en espèces',
        },
      },
    },
  },
  {
    _id: '#credit',
    displayValue: {
      object: {
        [BbitLanguage.EN_US]: {
          [BbitTranslationType.PLURAL]: 'Credit',
        },
        [BbitLanguage.DE_CH]: {
          [BbitTranslationType.PLURAL]: 'Guthaben',
        },
        [BbitLanguage.FR_CH]: {
          [BbitTranslationType.PLURAL]: 'Crédit',
        },
      },
    },
  },
];

@Component({
  selector: 'app-camping-booking-overlay',
  imports: [NgIcon, I18nPipe, AsyncPipe, BbitWebShopFormatPricePipe, LoadingComponent],
  templateUrl: './camping-booking-overlay.component.html',
  styleUrl: './camping-booking-overlay.component.scss',
})
export class CampingBookingOverlayComponent {
  public campsite = signal<IBbitCampsite | undefined>(undefined);
  public period = signal<IBbitCampsiteBookingPeriodWithCampsites | undefined>(undefined);
  public displayType = new BehaviorSubject<'book' | 'success' | 'error' | 'redirectToPayment' | 'loading'>('book');
  public paymentMethods = new BehaviorSubject<IBbitI18nSelectDataRow[]>([]);
  public paymentMethod = signal<string>('#invoice');
  private _onlinePayment = inject(BbitOnlinePaymentService);

  constructor(
    private _overlayRef: CampingBookingOverlayRef,
    private _website: BbitWebsiteService,
  ) {
    this.campsite.set(this._overlayRef.getData().campsite);
    this.period.set(this._overlayRef.getData().period);
    this.paymentMethods.next(
      allPaymentMethods.filter((method) => this.period()?.allowedPaymentMethods.includes(method._id)),
    );
    this.paymentMethod.set(this.period()?.allowedPaymentMethods[0] || '');
  }

  close(reload = false) {
    this._overlayRef.close(reload);
  }

  formatDate(date: string): string {
    return DateTime.fromISO(date).toFormat('dd.LL.yyyy HH:mm');
  }

  async book(): Promise<void> {
    this.displayType.next('loading');

    const body = {
      campsite: this.campsite()?._id,
      bookingPeriod: this.period()?._id,
      paymentMethod: this.paymentMethod(),
    };
    try {
      const response = await fetch(`${this._website.getBlApiUrl()}/campsite/book`, {
        method: 'POST',
        headers: {
          Authorization: `${this._website.getAccessToken()}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Booking failed');
      }
      const result = await response.json();

      if (result.isOnlinePayment) {
        if (result.invoice) {
          this._onlinePayment.pay({
            invoice: result.invoice,
            redirectUrl: `${window.location.origin}/shop/order-result`,
          });
          this.displayType.next('redirectToPayment');
          return;
        }
      }

      this.displayType.next('success');
    } catch (err) {
      this.displayType.next('error');
      console.error(err);
    }
  }
}
